/** Facets: <facets-form-component> wraps the filter form and exposes updateFilters(), called on each input change (partial swap in drawer mode), while <price-facet-component> clamps the price bounds first. */

import { sectionRenderer } from "@theme/section-renderer";
import { Component } from "@theme/component";
import { FilterUpdateEvent } from "@theme/events";
import { convertMoneyToMinorUnits, formatMoney } from "@theme/money-formatting";

const SEARCH_QUERY = "q";

/**
 * @extends {Component<{ facetsForm: HTMLFormElement }>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ["facetsForm"];

  /**
   * Construit les URL search params à partir du formulaire.
   * @param {FormData} [formData]
   * @returns {URLSearchParams}
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    const params = new URLSearchParams(/** @type any */ (formData));
    if (params.get("filter.v.price.gte") === "") params.delete("filter.v.price.gte");
    if (params.get("filter.v.price.lte") === "") params.delete("filter.v.price.lte");
    params.delete("page");

    const searchQuery = new URL(window.location.href).searchParams.get(SEARCH_QUERY) ?? "";
    if (searchQuery) params.set(SEARCH_QUERY, searchQuery);

    return params;
  }

  get sectionId() {
    const id = this.getAttribute("section-id");
    if (!id) throw new Error("section-id required on <facets-form-component>");
    return id;
  }

  /** Met à jour l'URL sans recharger. */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const params = this.createURLParameters();
    url.search = "";
    for (const [k, v] of params.entries()) url.searchParams.append(k, v);
    history.pushState({ urlParameters: params.toString() }, "", url.toString());
  }

  /** Called by each input onchange: partial swap in a drawer so it stays open, full section re-render otherwise. */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));

    if (this.dataset.drawer === "true") {
      this.#partialUpdate();
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  };

  /** « Tout effacer » empties the form then goes through updateFilters(), the same path as unchecking filters one by one; the hidden `filter.v.availability` checkbox is preserved since it is forced in the markup. */
  clearAllFilters = () => {
    for (const input of this.refs.facetsForm.querySelectorAll("input")) {
      if (input.name === "filter.v.availability") continue;
      if (input.type === "checkbox" || input.type === "radio") input.checked = false;
      else input.value = "";
    }
    this.updateFilters();
  };

  /** Applique une URL de filtres reçue de l'extérieur (ex: lien "retirer ce filtre"). */
  updateFiltersByURL(url) {
    history.pushState({}, "", url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));

    if (this.dataset.drawer === "true") {
      this.#partialUpdate();
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Identity of a control that survives the form swap: an option by name+value, an accordion trigger by its label.
   * @param {Element} el
   */
  #focusTarget(el) {
    if (el instanceof HTMLInputElement) return { kind: "input", name: el.name, value: el.value };
    const trigger = el.closest(".accordion__trigger");
    if (trigger) return { kind: "trigger", label: trigger.textContent?.trim() ?? "" };
    return null;
  }

  /**
   * Finds the equivalent control in the freshly rendered form.
   * @param {Element} form
   * @param {{ kind: string, name?: string, value?: string, label?: string }} target
   */
  #findFocusTarget(form, target) {
    if (target.kind === "input") {
      return [...form.querySelectorAll("input")].find(
        (input) => input.name === target.name && input.value === target.value,
      );
    }
    return [...form.querySelectorAll(".accordion__trigger")].find(
      (trigger) => trigger.textContent?.trim() === target.label,
    );
  }

  /** Drawer mode: only the product grid and the <form> are replaced, so `.filter_drawer` keeps `.is-open` and the slide-in does not replay. */
  async #partialUpdate() {
    const url = new URL(window.location.href);
    url.searchParams.set("section_id", this.sectionId);

    try {
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      // Grille produits.
      const newMain = doc.querySelector(".collection__main");
      const currentMain = document.querySelector(".collection__main");
      if (newMain && currentMain) currentMain.replaceWith(newMain);

      // Formulaire de filtres (états actifs, counts) — le drawer wrapper
      // reste en place.
      const newForm = doc.querySelector(".filter_form");
      const currentForm = this.querySelector(".filter_form");
      if (newForm && currentForm) {
        // The form is the scrolling container and carries the focused control: replacing it resets both, dropping a keyboard user out of the drawer and back to the top of the list on every toggle.
        const scrollTop = currentForm.scrollTop;
        const focusTarget = currentForm.contains(document.activeElement)
          ? this.#focusTarget(document.activeElement)
          : null;

        // Accordion open/closed state is preserved across the swap (the server only reopens filters holding active values), each one identified by its title label.
        const openLabels = new Set();
        currentForm.querySelectorAll("accordion-component").forEach((acc) => {
          if (acc.dataset.open === "true") {
            const label = acc.querySelector(".accordion__trigger")?.textContent?.trim();
            if (label) openLabels.add(label);
          }
        });
        // Applique l'état AVANT insertion pour que connectedCallback le lise.
        newForm.querySelectorAll("accordion-component").forEach((acc) => {
          const label = acc.querySelector(".accordion__trigger")?.textContent?.trim();
          if (label && openLabels.has(label)) acc.setAttribute("data-open", "true");
        });
        currentForm.replaceWith(newForm);
        newForm.scrollTop = scrollTop;
        // No preventScroll: the position above is restored first, so the browser only scrolls again if the control moved out of view.
        if (focusTarget) this.#findFocusTarget(newForm, focusTarget)?.focus();
      }

      // « Voir les résultats »: only its label is updated, never the element — drawer.js attaches the close listener on mount and a replaceWith would lose it.
      const newApply = doc.querySelector(".filter_apply");
      const currentApply = this.querySelector(".filter_apply");
      if (newApply && currentApply) currentApply.textContent = newApply.textContent;

      // Title count (« Titre (42) »), outside `.collection__main` so the grid swap misses it: copy its text only.
      const newCount = doc.querySelector("[data-products-count]");
      const currentCount = document.querySelector("[data-products-count]");
      if (newCount && currentCount) currentCount.textContent = newCount.textContent;

      // « Tout effacer »: the server computes its visibility through `hidden`, and the state is copied onto the element in place, like the results button.
      const newReset = doc.querySelector(".filter_reset");
      const currentReset = this.querySelector(".filter_reset");
      if (newReset && currentReset) currentReset.hidden = newReset.hidden;
    } catch (err) {
      console.error("[facets] partial update failed, falling back to full render:", err);
      sectionRenderer.renderSection(this.sectionId);
    }
  }
}

if (!customElements.get("facets-form-component")) {
  customElements.define("facets-form-component", FacetsFormComponent);
}

/**
 * @extends {Component<{ minInput: HTMLInputElement, maxInput: HTMLInputElement }>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */ currency;
  /** @type {string} */ moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("keydown", this.#onKeyDown);
    this.currency = this.dataset.currency ?? "USD";
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? "{{amount}}");
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keydown", this.#onKeyDown);
  }

  #extractMoneyPlaceholder(format) {
    const m = format.match(/{{\s*\w+\s*}}/);
    return m ? m[0] : "{{amount}}";
  }

  /** Restreint la saisie au numérique + touches de navigation. */
  #onKeyDown = (event) => {
    if (event.metaKey) return;
    const allowed = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(allowed)) event.preventDefault();
  };

  /** Appelé par onchange des inputs prix. Clamp puis update du form parent. */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;
    this.#clampInput(minInput);
    this.#clampInput(maxInput);

    const form = this.closest("facets-form-component");
    if (form instanceof FacetsFormComponent) form.updateFilters();
  }

  #clampInput(input) {
    if (input.value.trim() === "") return;
    const value = convertMoneyToMinorUnits(input.value, this.currency) ?? 0;
    const min = convertMoneyToMinorUnits(input.getAttribute("data-min") ?? "0", this.currency) ?? 0;
    const max = convertMoneyToMinorUnits(input.getAttribute("data-max") ?? "0", this.currency) ?? 0;
    if (value < min) input.value = formatMoney(min, this.moneyFormat, this.currency);
    else if (value > max) input.value = formatMoney(max, this.moneyFormat, this.currency);
  }
}

if (!customElements.get("price-facet-component")) {
  customElements.define("price-facet-component", PriceFacetComponent);
}
